import React, { useEffect, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import DateTimePicker from '../../components/DateTimePicker';
import { TanTable } from '../../components/TanTable';
import { formatCurrency } from '../../utils/money';
import { ACCRUAL_REGIONS, accrualInvoiceHref, getDriverPayrollAccrual, type AccrualQuery, type AccrualResponse, type AccrualRow } from '../../services/driverPayrollAccrual.service';
import { calendarDate, dateParam, reportError, REPORT_BUTTON } from './reports/reportUtils';

export const ACCRUAL_TITLE = 'Accrual For Driver Payroll';

export function DriverPayrollAccrualForm({ onRun }: { onRun: (query: AccrualQuery) => void }) {
  const [begin, setBegin] = useState<Date | null>(null);
  const [end, setEnd] = useState<Date | null>(null);
  const [showMules, setShowMules] = useState('Y');
  const [error, setError] = useState('');
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!begin || !end) { setError('Enter both begin and end dates.'); return; }
    if (dateParam(end) < dateParam(begin)) { setError('End date must be on or after begin date.'); return; }
    setError('');
    onRun({ begin: dateParam(begin), end: dateParam(end), show_mules: showMules === 'Y' });
  };
  return <form onSubmit={submit} className="p-3 space-y-3 text-label">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <DateTimePicker mode="date" labelType="custom" label="Begin Date" ariaLabel="Begin Date" value={begin} onChange={v => setBegin(calendarDate(v))} />
      <DateTimePicker mode="date" labelType="custom" label="End Date" ariaLabel="End Date" value={end} onChange={v => setEnd(calendarDate(v))} />
    </div>
    <div className="flex flex-wrap items-end gap-4">
      <label className="text-sm">Show Mules
        <select aria-label="Show Mules" className="ml-2 rounded border border-theme-border-primary bg-theme-bg-primary p-2" value={showMules} onChange={e => setShowMules(e.target.value)}>
          <option value="Y">Yes</option><option value="N">No</option>
        </select>
      </label>
      <button type="submit" className={REPORT_BUTTON}>Continue</button>
    </div>
    {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
  </form>;
}

const moneyColumn = (accessorKey: string, header: string): ColumnDef<AccrualRow> => ({ accessorKey, header, size: 120,
  cell: ({ getValue }) => formatCurrency(getValue()), meta: { aggType: 'sum' } });
const columns: ColumnDef<AccrualRow>[] = [
  { accessorKey: 'billtoname', header: 'Bill To', size: 230 },
  { accessorKey: 'delivery_date', header: 'Delivery Date', size: 170, meta: { isDate: true, showTime: true } },
  { accessorKey: 'terminate_date', header: 'Terminate Date', size: 170, meta: { isDate: true, showTime: true } },
  { accessorKey: 'invoice_date', header: 'Invoice Date', size: 130, meta: { isDate: true, showTime: false } },
  { accessorKey: 'pronumber', header: 'PRO', size: 110 },
  moneyColumn('dray', 'Dray'), moneyColumn('fuel', 'Fuel'), moneyColumn('handling', 'Handling'),
  moneyColumn('invoicetotal', 'Invoice Total'), moneyColumn('creditamt', 'Credit Amount'), moneyColumn('invoice_net', 'Invoice Net'),
  ...ACCRUAL_REGIONS.map(([key, label]) => moneyColumn(key, label)),
  moneyColumn('allocation_difference', 'Allocation Difference'),
  { accessorKey: 'errors', header: 'Errors', size: 330, cell: ({ getValue }) => <span className="whitespace-pre-wrap text-red-600 dark:text-red-400">{String(getValue() ?? '')}</span> },
  { id: 'listinvoice', header: 'Invoice', size: 115, enableSorting: false, enableColumnFilter: false,
    cell: ({ row }) => <a href={accrualInvoiceHref(row.original)} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">List Invoice</a> },
];

export function DriverPayrollAccrualResults({ query }: { query: AccrualQuery }) {
  const [result, setResult] = useState<AccrualResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const queryKey = JSON.stringify(query);
  useEffect(() => {
    let active = true;
    setLoading(true); setError(null); setResult(null);
    void getDriverPayrollAccrual(JSON.parse(queryKey))
      .then(data => { if (active) setResult(data); })
      .catch(e => { if (active) setError(reportError(e, 'Unable to load the accrual report. Please try again.')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [queryKey, refresh]);
  return <div className="h-full min-h-0 flex flex-col">
    <div className="flex-shrink-0 p-3 text-sm text-label space-y-2 max-h-[35vh] overflow-auto">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p>Deliveries: {query.begin} – {query.end} · Show Mules: {query.show_mules ? 'Yes' : 'No'}</p>
        <button type="button" className={REPORT_BUTTON} onClick={() => setRefresh(v => v + 1)} disabled={loading}>Refresh Report</button>
      </div>
      {result && <>
        <p>Uninvoiced or invoiced in {result.invoiceMonth}.</p>
        <div className="flex flex-wrap gap-x-6 gap-y-1 font-semibold" aria-label="Report totals">
          <span>Invoice total: {formatCurrency(result.invoiceTotal)}</span>
          <span>Errors: {result.errorCount}</span><span>Errors total: {formatCurrency(result.errorsTotal)}</span>
        </div>
        <details><summary className="cursor-pointer">Regional totals and calculation notes</summary>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">{ACCRUAL_REGIONS.map(([key, label]) => <span key={key}>{label}: {formatCurrency(result.totals[key] ?? 0)}</span>)}</div>
          <p className="mt-2 text-muted">Report totals cover all returned rows. As in v1, regional allocations apply the credit to each charge line; Invoice total deducts it once per PRO. Errors total compares gross invoice amounts with allocations to the displayed regions.</p>
        </details>
      </>}
    </div>
    <div className="flex-1 min-h-0">
      <TanTable<AccrualRow> settingsKey="accounting-driver-payroll-accrual" data={result?.rows ?? []} columns={columns}
        getRowId={row => String(row.pronumber)} cardTitle="Accrual Search" loading={loading} errorMessage={error} fillContainer enableColumnFilters />
    </div>
  </div>;
}
